/**
 * useInfiniteConversations - Infinite Scroll Hook para Conversas
 * Implementa carregamento incremental com paginação baseada em tipo
 */

import { useInfiniteQuery } from '@tanstack/react-query';
import { chatService } from '../services';
import type { ConversationQuery } from '@troia-v3/types';

export interface UseInfiniteConversationsOptions {
  /** Tipo de conversa para filtrar */
  conversationType: 'individual' | 'group' | 'ai';
  /** Limite de itens por página */
  limit?: number;
  /** Habilitar/desabilitar query */
  enabled?: boolean;
  /** Filtros adicionais */
  additionalFilters?: ConversationQuery['filters'];
}

/**
 * Hook para infinite scroll de conversas com paginação por tipo
 *
 * @example
 * ```tsx
 * const {
 *   data,
 *   fetchNextPage,
 *   hasNextPage,
 *   isFetchingNextPage,
 *   isLoading
 * } = useInfiniteConversations({
 *   conversationType: 'individual',
 *   limit: 25
 * });
 *
 * // Todas as conversas flat
 * const conversations = data?.pages.flatMap(page => page.items) ?? [];
 * ```
 */
export const useInfiniteConversations = (options: UseInfiniteConversationsOptions) => {
  const {
    conversationType,
    limit = 25,
    enabled = true,
    additionalFilters = {}
  } = options;

  return useInfiniteQuery({
    queryKey: ['conversations', 'infinite', conversationType, additionalFilters],
    queryFn: async ({ pageParam, signal }) => {
      const query: ConversationQuery = {
        page: pageParam as number,
        limit,
        filters: {
          conversationType, // ✅ Filtro principal
          status: ['waiting', 'active'], // ✅ Apenas conversas aguardando ou em atendimento
          excludeSource: 'email', // ✅ Excluir conversas de email do chat
          ...additionalFilters
        },
        sortBy: 'lastMessageAt',  // ✅ Ordenar por última mensagem (mais recente primeiro)
        sortOrder: 'desc'
      };

      return chatService.getConversations(query, signal);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: any) => {
      // Se há mais páginas, retorna próximo número
      if (lastPage.page < lastPage.totalPages) {
        return lastPage.page + 1;
      }
      // Sem mais páginas
      return undefined;
    },
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes (era cacheTime)
    enabled,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false
  });
};
